import {
	faBan,
	faExclamationTriangle,
	faPencil,
	faProjectDiagram,
	faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Editor from "@monaco-editor/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import { useSystemRoleStore } from "@/store/systemRole.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddFlowDefinition from "./AddFlowDefinition";

export interface FlowDefinition {
	id: string;
	flowActionId: string;
	flowTargetId: string;
	description: string;
	code: string;
	createdAt: string;
	updatedAt: string;
}

interface FlowAction {
	id: string;
	name: string;
	onClose: () => void;
}

const FLOW_ACTIONS_QUERY_KEY = (flowTypeId: string) =>
	["flow-actions", flowTypeId] as const;
const FLOW_DEFINITION_QUERY_KEY = (flowTargetId: string, actionId: string) =>
	["flow-definition", flowTargetId, actionId] as const;

const ViewFlowDefinition: React.FC = () => {
	const { success } = useToast();
	const { flowTypeId, flowTargetId } = useParams<{
		flowTypeId: string;
		flowTargetId: string;
	}>();
	const [selectedAction, setSelectedAction] = useState<string>("");
	const [isEditing, setIsEditing] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [isConfirmSwitchModalOpen, setIsConfirmSwitchModalOpen] =
		useState(false);
	const [nextActionId, setNextActionId] = useState<string | null>(null);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const handleGoToFlowActions = () => {
		navigate(`/admin/integrations/${flowTypeId}`);
	};

	// Fetch actions
	const {
		data: actions,
		isLoading: isLoadingActions,
		isError: isActionsError,
		error: actionsError
	} = useQuery({
		queryKey: FLOW_ACTIONS_QUERY_KEY(flowTypeId || ""),
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_ACTIONS", "LIST"], flowTypeId)
			);
			return data as FlowAction[];
		}
	});

	const { isDeleteModalOpen, setIsDeleteModalOpen } = useSystemRoleStore();

	// Handle the success case with useEffect
	useEffect(() => {
		if (actions && actions.length > 0 && !selectedAction) {
			setSelectedAction(actions[0].id);
		}
	}, [actions, selectedAction]);

	// Fetch flow definition for selected action
	const {
		data: currentDefinition,
		isLoading: isLoadingDefinition,
		isError: isDefinitionError,
		error: definitionError
	} = useQuery({
		queryKey: FLOW_DEFINITION_QUERY_KEY(
			flowTargetId || "",
			selectedAction || ""
		),
		queryFn: async () => {
			if (!selectedAction) return null;

			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_DEFINITIONS", "LIST"])
			);

			const definition = data.find(
				(def: FlowDefinition) =>
					def.flowTargetId === flowTargetId &&
					def.flowActionId === selectedAction
			);

			return definition || null;
		},
		enabled: !!selectedAction && !!flowTargetId
	});

	// Delete definition mutation
	const deleteMutation = useMutation({
		mutationFn: (id: string) =>
			apiClient.delete(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_DEFINITIONS", "DELETE_BY_ID"], id)
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: FLOW_DEFINITION_QUERY_KEY(
					flowTargetId || "",
					selectedAction || ""
				)
			});
			success("Flow definition deleted successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete flow definition");
		}
	});

	if (!flowTypeId || !flowTargetId) {
		return (
			<div className="p-4 text-red-600">Invalid flow type or target ID</div>
		);
	}

	const handleActionChange = (actionId: string) => {
		if ((isEditing || isCreating) && actionId !== selectedAction) {
			setNextActionId(actionId);
			setIsConfirmSwitchModalOpen(true);
		} else {
			setSelectedAction(actionId);
		}
	};

	const confirmTabSwitch = () => {
		if (nextActionId) {
			setSelectedAction(nextActionId);
		}
		setNextActionId(null);
		setIsEditing(false);
		setIsCreating(false);
		setIsConfirmSwitchModalOpen(false);
	};

	const cancelTabSwitch = () => {
		setNextActionId(null);
		setIsConfirmSwitchModalOpen(false);
	};

	const handleEditToggle = () => {
		setIsEditing(true);
		setIsCreating(false);
	};

	const handleCreateToggle = () => {
		setIsCreating(true);
		setIsEditing(false);
	};

	const handleCloseForm = () => {
		setIsEditing(false);
		setIsCreating(false);
	};

	const handleDelete = () => {
		if (!currentDefinition) return;

		if (!currentDefinition || isDeleteModalOpen) return;
		setIsDeleteModalOpen(true);
	};

	if (isLoadingActions || isLoadingDefinition || deleteMutation.isPending) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	if (isActionsError) {
		handleApiError(actionsError as ApiError, "Failed to load actions");
		return <div className="p-4 text-red-600">Error loading actions</div>;
	}

	if (isDefinitionError) {
		handleApiError(
			definitionError as ApiError,
			"Failed to load flow definition"
		);
		return (
			<div className="p-4 text-red-600">Error loading flow definition</div>
		);
	}

	// Show notification when no actions are available
	if (!actions || actions.length === 0) {
		return (
			<div className="w-full">
				<div className="flex flex-col items-center justify-center py-12 text-center p-8 border rounded-sm shadow bg-white h-[600px]">
					<div className="text-5xl text-gray-300 mb-4">
						<FontAwesomeIcon icon={faBan} className="text-red-500" />
					</div>
					<h3 className="text-xl font-medium text-gray-700 mb-2">
						No Actions Available
					</h3>
					<p className="text-gray-500 w-full sm:w-[480px] mb-6">
						There are no actions defined for this flow type. You need to create
						actions first before you can define flow definitions.
					</p>
					<button
						type="button"
						onClick={handleGoToFlowActions}
						className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
					>
						Go to Actions
					</button>
				</div>
			</div>
		);
	}

	const hasDefinition = !!currentDefinition;

	return (
		<div className="w-full">
			<div className="flex items-center justify-between p-0 bg-gray-50">
				<div className="flex items-center space-x-4">
					{actions?.map((action: FlowAction) => {
						const isActive = selectedAction === action.id;
						return (
							<button
								type="button"
								key={action.id}
								onClick={() => handleActionChange(action.id)}
								className={`px-0 py-2 text-sm transition-all duration-150 text-gray-600 
          underline underline-offset-8 focus:outline-0	
          ${isActive ? "decoration-red-600 text-gray-900" : "decoration-transparent"}
          hover:decoration-none border-0
        `}
							>
								{action.name}
							</button>
						);
					})}
				</div>

				<div className="flex items-center space-x-4 relative">
					{!isEditing && !isCreating && (
						<>
							<button
								type="button"
								onClick={hasDefinition ? handleEditToggle : handleCreateToggle}
								className="px-3 py-2 bg-gray-500 text-white rounded text-sm hover:bg-blue-700"
							>
								<FontAwesomeIcon icon={faPencil} className="txt-sm mr-2" />
								{hasDefinition ? "Edit" : "Create"}
							</button>

							{hasDefinition && (
								<button
									type="button"
									onClick={handleDelete}
									className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
								>
									Delete
								</button>
							)}
						</>
					)}
				</div>
			</div>

			<div className="py-4 bg-gray-50 space-y-4 px-0">
				{isEditing || isCreating ? (
					<AddFlowDefinition
						flowTargetId={flowTargetId}
						flowActionId={selectedAction}
						definition={isEditing ? currentDefinition : null}
						onClose={handleCloseForm}
					/>
				) : (
					<>
						{currentDefinition?.description && (
							<div className="flex items-center">
								<span className="text-sm font-medium text-gray-700 inline-block mr-3">
									Description:
								</span>
								<p className="text-gray-900 font-bold">
									{currentDefinition.description}
								</p>
							</div>
						)}

						{currentDefinition && (
							<Editor
								className="border border-gray-300 rounded-md shadow"
								height="500px"
								defaultLanguage="typescript"
								value={currentDefinition.code || ""}
								options={{
									readOnly: true,
									minimap: { enabled: false },
									fontSize: 14,
									padding: { top: 10 },
									lineNumbers: "on",
									scrollBeyondLastLine: false,
									wordWrap: "on"
								}}
							/>
						)}

						{!currentDefinition && (
							<div className="border flex flex-col h-[500px] items-center justify-center my-auto rounded-md shadow text-center">
								<div className="flex items-center space-x-2 text-gray-600 mb-4">
									<FontAwesomeIcon
										icon={faProjectDiagram}
										className="text-6xl text-gray-400 w-[35px]"
									/>
								</div>
								<h3 className="text-xl font-medium text-gray-700 mb-2">
									No Flow Definition Found
								</h3>
								<p className="text-gray-500 mb-6 w-full sm:w-[480px]">
									There is no flow definition for this action yet. Click the
									"Create" button above to create a new flow definition.
								</p>
							</div>
						)}
					</>
				)}
			</div>
			{isDeleteModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Delete Definition
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to delete this definition?
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => {
										setIsDeleteModalOpen(false);
										deleteMutation.mutate(currentDefinition.id);
									}}
								>
									Delete
								</button>
								<button
									type="button"
									onClick={() => setIsDeleteModalOpen(false)}
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			{/* Switch tabs when edit create time, confirmation */}
			{isConfirmSwitchModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Switch Tabs?
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												You have unsaved changes. Are you sure you want to
												switch tabs?
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									onClick={confirmTabSwitch}
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
								>
									Yes, Switch
								</button>
								<button
									type="button"
									onClick={cancelTabSwitch}
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ViewFlowDefinition;
